import {loggerFactory} from "@cm/browser-log"
import {Injector} from "@angular/core"
import {Settings} from "@app/common/models/settings/settings"
import {RenderingService} from "@app/common/services/rendering/rendering.service"
import {postProcessingGraph} from "@cm/image-processing/operations/render-post-processing"
import {JobNodes} from "@cm/job-nodes"
import {ImageProcessingInput, makeImageProcessingTaskDescriptor} from "@cm/job-nodes/image-processing"
import {PictureRenderJobOutput, PictureRenderJobOutputSchema, RenderMetadata, RenderMetadataSchema} from "@cm/job-nodes/rendering"
import {Color, SceneNodes, Parameters, COMPOSITING_PASSES} from "@cm/template-nodes"
import {jsonToGraph} from "@cm/browser-utils"
import {sortedJSONStringify} from "@cm/utils/sorted-json-stringify"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {isCryptomattePass} from "@cm/render-nodes"
import {ContentTypeModel, ImageColorSpace, JobState, JobType} from "@generated"
import {
    CreateJobAssignmentForTemplateImageViewerGQL,
    DeleteJobForTemplateImageViewerGQL,
    GetDataObjectDetailsForTemplateImageViewerGQL,
    GetJobAssignmentsDetailsForTemplateImageViewerGQL,
    JobDetailsForTemplateImageViewerFragment,
} from "@template-editor/components/template-image-viewer/template-image-viewer.component.generated"
import {z} from "zod"
import {ImageProcessingNodes, PostProcessingInputData, PostProcessingSettings} from "@cm/image-processing-nodes"
import {Utility} from "@cm/job-nodes/utility"
import {DataObject, extensionForContentType, promiseAllProperties} from "@cm/utils"
import filenamify from "filenamify/browser"

const templateJobsLogger = loggerFactory.getLogger("jobs")

export type JobData = JobDetailsForTemplateImageViewerFragment
export type UnassignedJobData = {
    displayName: string
    type: "render"
    job: JobData
}

export type PostProcessedImageMediaType = "image/tiff" | "image/png" | "image/jpeg"
export type BatchExportMediaType = PostProcessedImageMediaType | "image/x-exr"

export function isJobError(job: JobData) {
    switch (job.state) {
        case JobState.Failed:
        case JobState.Cancelled:
            return true
        default:
            return false
    }
}

export function isJobPending(job: JobData) {
    switch (job.state) {
        case JobState.Init:
        case JobState.Running:
        case JobState.Runnable:
            return true
        default:
            return false
    }
}

export const getConfigurationString = (parameters: Parameters) => sortedJSONStringify(parameters.parameters)

export const parseAssignmentKey = (key: string) => {
    if (!key.includes(":")) throw Error(`Failed to parse configuration job assignment key (missing colon): ${key}`)
    const parsedType = z.union([z.literal("render"), z.literal("postProcess")]).safeParse(key.split(":")[0])
    if (!parsedType.success) throw Error(`Failed to parse configuration job assignment key (invalid job type): ${key}`)

    const configurationParameters = (() => {
        try {
            const json = JSON.parse(key.substring(`${parsedType.data}:`.length))
            const parsedParameters = z.record(z.string(), z.string()).safeParse(json)
            if (!parsedParameters.success) throw Error("Invalid schema")
            return new Parameters({parameters: parsedParameters.data})
        } catch (error) {
            templateJobsLogger.warn(`Unable to parse configuration parameters from job assignment: ${key}, error: ${error}`)
            return undefined
        }
    })()
    return {type: parsedType.data, configurationParameters}
}

export const getAssignmentKey = (type: "render" | "postProcess", configurationString: string) => `${type}:${configurationString}`

export async function getJobAssignment(injector: Injector, templateRevisionId: string, assignmentKey: string) {
    const jobAssignmentGql = injector.get(GetJobAssignmentsDetailsForTemplateImageViewerGQL)
    const result = await fetchThrowingErrors(jobAssignmentGql)({
        filter: {
            objectId: templateRevisionId,
            contentTypeModel: ContentTypeModel.TemplateRevision,
            assignmentKey: {equals: assignmentKey},
        },
    })

    return result.jobAssignments[0]?.job
}

export async function getAllJobAssignments(injector: Injector, templateRevisionId: string) {
    const jobAssignmentGql = injector.get(GetJobAssignmentsDetailsForTemplateImageViewerGQL)
    const result = await fetchThrowingErrors(jobAssignmentGql)({
        filter: {
            objectId: templateRevisionId,
            contentTypeModel: ContentTypeModel.TemplateRevision,
        },
    })
    return result.jobAssignments
}

export async function getJobAssignments<T extends readonly string[]>(
    injector: Injector,
    templateRevisionId: string,
    assignmentKeys: [...T],
): Promise<{[K in keyof T]: JobData | undefined}> {
    const jobAssignmentGql = injector.get(GetJobAssignmentsDetailsForTemplateImageViewerGQL)
    const result = await fetchThrowingErrors(jobAssignmentGql)({
        filter: {
            objectId: templateRevisionId,
            contentTypeModel: ContentTypeModel.TemplateRevision,
            assignmentKey: {in: assignmentKeys},
        },
    })

    return assignmentKeys.map((key) => result.jobAssignments.find((assignment) => assignment?.assignmentKey === key)?.job) as {
        [K in keyof T]: JobData | undefined
    }
}

export const createRenderJob = async (
    injector: Injector,
    renderingService: RenderingService,
    templateRevisionId: string,
    configurationString: string,
    sceneNodes: SceneNodes.SceneNode[],
    organizationLegacyId: number,
    queueGroup?: string,
) => {
    const assignmentKey = getAssignmentKey("render", configurationString)

    const existingRenderJob = await getJobAssignment(injector, templateRevisionId, assignmentKey)
    if (existingRenderJob) throw Error(`A render job already exists for this variation. (id: ${existingRenderJob.id})`)

    const jobName = `Render template ${templateRevisionId} (variation)`

    const renderJob = await renderingService.submitRenderJob({
        nodes: sceneNodes,
        fullQuality: true,
        passes: COMPOSITING_PASSES,
        name: jobName,
        organizationLegacyId,
        queueGroup,
    })

    const createJobAssignmentGql = injector.get(CreateJobAssignmentForTemplateImageViewerGQL)
    const jobAssignment = (
        await mutateThrowingErrors(createJobAssignmentGql)({
            input: {
                objectId: templateRevisionId,
                contentTypeModel: ContentTypeModel.TemplateRevision,
                assignmentKey,
                jobId: renderJob.id,
            },
        })
    ).createJobAssignment

    const createdJob = await getJobAssignment(injector, templateRevisionId, assignmentKey)
    if (!createdJob) throw Error("Failed to create render job")

    return [jobAssignment, createdJob] as const
}

const deleteJob = async (injector: Injector, templateRevisionId: string, assignmentKey: string) => {
    const job = await getJobAssignment(injector, templateRevisionId, assignmentKey)
    if (!job) return undefined

    const deleteJobGql = injector.get(DeleteJobForTemplateImageViewerGQL)
    return mutateThrowingErrors(deleteJobGql)({id: job.id})
}

export const deleteRenderJob = async (injector: Injector, templateRevisionId: string, configurationString: string) => {
    const assignmentKey = getAssignmentKey("render", configurationString)
    return deleteJob(injector, templateRevisionId, assignmentKey)
}

export const postProcessingSettingsToImageProcessingSettings = (settings: SceneNodes.RenderPostProcessingSettings): PostProcessingSettings => {
    const {backgroundColor, mask, ...rest} = settings

    function isColorTuple(x: Color | DataObject | undefined): x is Color {
        return Array.isArray(x) && x.length === 3 && x.every((num) => typeof num === "number")
    }

    return {
        ...rest,
        backgroundColor:
            isColorTuple(backgroundColor) || !backgroundColor
                ? backgroundColor
                : {
                      type: "decode",
                      input: {
                          type: "externalData",
                          sourceData: {
                              type: "dataObjectReference",
                              dataObjectId: backgroundColor.legacyId,
                          },
                          resolveTo: "encodedData",
                      },
                  },
        mask: mask
            ? {
                  type: "decode",
                  input: {
                      type: "externalData",
                      sourceData: {
                          type: "dataObjectReference",
                          dataObjectId: mask.legacyId,
                      },
                      resolveTo: "encodedData",
                  },
              }
            : undefined,
    }
}

export const createPostProcessTask = async (
    injector: Injector,
    templateRevisionId: string,
    configurationString: string,
    mediaType: PostProcessedImageMediaType,
    postProcessingSettings: SceneNodes.RenderPostProcessingSettings | undefined,
    abortSignal?: AbortSignal,
) => {
    const [existingRenderJob] = await getJobAssignments(injector, templateRevisionId, [getAssignmentKey("render", configurationString)])
    if (!existingRenderJob) throw Error(`No existing render job`)

    if (isJobPending(existingRenderJob) || isJobError(existingRenderJob)) throw Error(`Render job is not complete, state is ${existingRenderJob.state}`)

    const {output} = existingRenderJob
    const graph = jsonToGraph(output)
    const renderOutput = PictureRenderJobOutputSchema.parse(graph)
    if (!renderOutput) throw Error("Render job output is null")
    if (!renderOutput.renderPasses) throw Error("Render job has no render passes")

    const postProcessingInput = await getPostProcessingInput(renderOutput, injector, false, abortSignal)

    const input: ImageProcessingInput = {
        graph: {
            type: "encode",
            mediaType,
            input: {
                type: "convert",
                input: postProcessingGraph(
                    postProcessingInput,
                    postProcessingSettings ? postProcessingSettingsToImageProcessingSettings(postProcessingSettings) : {},
                ).image,
                channelLayout: "RGBA",
                dataType: "uint8",
                sRGB: true,
            },
        },
    }

    return JobNodes.task(makeImageProcessingTaskDescriptor<JobNodes.DataObjectReference>(), {input: JobNodes.value(input)})
}

const getCombinedPassReference = async (injector: Injector, templateRevisionId: string, configurationString: string) => {
    const [existingRenderJob] = await getJobAssignments(injector, templateRevisionId, [getAssignmentKey("render", configurationString)])
    if (!existingRenderJob) throw Error(`No existing render job`)

    if (isJobPending(existingRenderJob) || isJobError(existingRenderJob)) throw Error(`Render job is not complete, state is ${existingRenderJob.state}`)

    const {output} = existingRenderJob
    const graph = jsonToGraph(output)
    const renderOutput = PictureRenderJobOutputSchema.parse(graph)
    if (!renderOutput) throw Error("Render job output is null")
    if (!renderOutput.renderPasses) throw Error("Render job has no render passes")

    const combinedPass = renderOutput.renderPasses["Combined"]
    if (!combinedPass) throw Error("Render job has no combined pass")

    return combinedPass
}

export const createBatchExportJobGraph = async (
    injector: Injector,
    configurations: {configurationString: string; name: string; postProcessingSettings: SceneNodes.RenderPostProcessingSettings | undefined}[],
    mediaType: BatchExportMediaType,
    templateRevisionId: string,
    organizationLegacyId: number,
    exportFilename: string,
) => {
    const isExr = mediaType === "image/x-exr"

    const zipFiles: Record<string, JobNodes.TypedDataNode<Utility.Zip.File>> = {}
    for (const {configurationString, name, postProcessingSettings} of configurations) {
        const sanitizedName = filenamify(name, {replacement: "-"})
        const ext = extensionForContentType(mediaType)
        let filename = `${sanitizedName}.${ext}`
        let counter = 1
        while (filename in zipFiles) {
            filename = `${sanitizedName}.${String(counter).padStart(3, "0")}.${ext}`
            counter++
        }

        if (isExr) {
            const combinedPassRef = await getCombinedPassReference(injector, templateRevisionId, configurationString)
            zipFiles[filename] = JobNodes.struct({type: JobNodes.value(Utility.Zip.FILE), content: JobNodes.value(combinedPassRef)})
        } else {
            const task = await createPostProcessTask(injector, templateRevisionId, configurationString, mediaType, postProcessingSettings)
            zipFiles[filename] = JobNodes.struct({type: JobNodes.value(Utility.Zip.FILE), content: task})
        }
    }

    const zipTask = JobNodes.task(Utility.Zip.task, {
        input: JobNodes.struct({
            filename: JobNodes.value(exportFilename),
            destination: JobNodes.value({
                type: "tmpDownloadData" as const,
                customerId: organizationLegacyId,
            }),
            root: JobNodes.struct({
                type: JobNodes.value(Utility.Zip.FOLDER),
                content: JobNodes.struct(zipFiles),
            }),
        }),
    })

    return JobNodes.jobGraph<Utility.Zip.Output>(zipTask, {
        platformVersion: Settings.APP_VERSION,
    })
}

export const getPostProcessingInput = async (
    renderOutput: PictureRenderJobOutput,
    injector: Injector,
    inMemory: boolean,
    abortSignal?: AbortSignal,
): Promise<PostProcessingInputData> => {
    const dataObjectGql = injector.get(GetDataObjectDetailsForTemplateImageViewerGQL)

    const getImage = async (dataObjectReference: JobNodes.DataObjectReference): Promise<ImageProcessingNodes.ImageNode> => {
        if (inMemory) {
            const {dataObject} = await fetchThrowingErrors(dataObjectGql)(
                typeof dataObjectReference.dataObjectId === "number" ? {legacyId: dataObjectReference.dataObjectId} : {id: dataObjectReference.dataObjectId},
            )
            const {mediaType, imageColorSpace, downloadUrl} = dataObject

            if (!mediaType) throw new Error("No media type found")
            if (imageColorSpace !== ImageColorSpace.Linear && imageColorSpace !== ImageColorSpace.Srgb) {
                throw Error(`Cannot resolve external data of unsupported color space: ${imageColorSpace}`)
            }

            const response = await fetch(downloadUrl, {signal: abortSignal})
            if (!response.ok) {
                throw new Error(`Failed to download data: ${response.statusText} for image data object ${dataObjectReference.dataObjectId}`)
            }

            const data = new Uint8Array(await response.arrayBuffer())

            return {
                type: "decode",
                input: {
                    type: "externalData",
                    resolveTo: "encodedData",
                    sourceData: dataObjectReference,
                    resolvedData: {
                        data,
                        mediaType,
                        colorSpace: imageColorSpace === ImageColorSpace.Linear ? "linear" : "sRGB",
                    },
                },
            }
        } else return {type: "decode", input: {type: "externalData", resolveTo: "encodedData", sourceData: dataObjectReference}}
    }

    const getRenderMetadata = async (dataObjectReference: JobNodes.DataObjectReference): Promise<RenderMetadata> => {
        const {dataObject} = await fetchThrowingErrors(dataObjectGql)(
            typeof dataObjectReference.dataObjectId === "number" ? {legacyId: dataObjectReference.dataObjectId} : {id: dataObjectReference.dataObjectId},
        )
        const {downloadUrl} = dataObject

        const response = await fetch(downloadUrl, {signal: abortSignal})
        if (!response.ok) {
            throw new Error(`Failed to download data: ${response.statusText} for metadata object ${dataObjectReference.dataObjectId}`)
        }

        const data = await response.json()
        return RenderMetadataSchema.parse(data)
    }

    if (!renderOutput) throw Error("No render output provided")

    if (renderOutput.renderPasses) {
        const {renderPasses, aoShadowMaskPass, metadata} = renderOutput

        const {Combined: combinedPass, ShadowCatcher: shadowCatcherPass} = renderPasses
        if (!combinedPass) throw Error("Job output has no combined render pass")

        return promiseAllProperties({
            combinedPass: getImage(combinedPass),
            shadowCatcherPass: shadowCatcherPass ? getImage(shadowCatcherPass) : undefined,
            shadowMaskPass: aoShadowMaskPass ? getImage(aoShadowMaskPass) : undefined,
            maskData: metadata
                ? (async () => {
                      //NOTE: all cryptomatte passes use a common ID space, so the list order and type (material, asset, etc.) does not matter
                      const pendingCryptoPasses = Object.entries(renderPasses)
                          .filter((entry): entry is [string, JobNodes.DataObjectReference] => {
                              const [passName, dataObjectRef] = entry
                              return isCryptomattePass(passName) && dataObjectRef !== undefined
                          })
                          .map(([_, dataObjectRef]) => getImage(dataObjectRef))

                      if (pendingCryptoPasses.length === 0) return undefined
                      return {
                          cryptoPasses: await Promise.all(pendingCryptoPasses),
                          cryptoManifest: (await getRenderMetadata(metadata)).cryptomatteManifest,
                      }
                  })()
                : undefined,
        })
    } else if (renderOutput.preview) {
        const image = await getImage(renderOutput.preview)
        return {combinedPass: image}
    } else throw Error("Job output has no valid renderPasses or preview")
}
